import type { components } from '$lib/api/schema';
import { formatAmount } from './amount';

export type UnitConversion = components['schemas']['UnitConversionDto'];
export type PlatformUnit = components['schemas']['UnitResponse'];

/**
 * The platform's units as the app works with them (they come from the server, `GET /api/units`, and only
 * super-admins change them). Mirrors the server's `UnitSystem`: a base unit's amounts are summed in its base
 * (kg → g), and shown in the most readable active unit of their measure; a unit the catalogue does not know is a
 * unit of its own.
 */
export interface UnitSystem {
	/** The units offered for new data, in order. */
	readonly active: readonly string[];
	/** The base unit of a unit: "kg" → "g", "dl" → "ml", "stk" → "stk". */
	baseUnit(unit: string): string;
	/** Whether a unit is counted in whole pieces (stk, pk …), so the pantry takes one at a time. */
	isCounted(unit: string): boolean;
	/** An amount in its base unit: 1,5 kg → 1500 (g). */
	inBase(amount: number, unit: string): number;
	/**
	 * An amount in its most readable unit: of the base unit (size 1) and the active units based on it, the largest it
	 * fills at least once: 1500 g → "1,5 kg", 100 ml → "1 dl", 500 g stays g though mg fits too.
	 */
	readable(amount: number, unit: string): string;
	/** The fixed kitchen measures as conversions every item has, "1 spsk = 15 ml". */
	readonly fixedConversions: readonly UnitConversion[];
}

export function unitSystem(units: readonly PlatformUnit[]): UnitSystem {
	const byCode = new Map(units.map((unit) => [unit.code, unit]));
	const baseLink = (unit: string) => {
		const link = byCode.get(unit)?.link;
		return link?.kind === 'base' ? link : null;
	};
	const baseUnit = (unit: string) => baseLink(unit)?.unit ?? unit;
	const inBase = (amount: number, unit: string) => amount * (baseLink(unit)?.factor ?? 1);
	return {
		active: units.filter((unit) => unit.active).map((unit) => unit.code),
		baseUnit,
		isCounted: (unit) => byCode.get(unit)?.countable ?? false,
		inBase,
		fixedConversions: units.flatMap(({ code, link }) =>
			link?.kind === 'fixed'
				? [{ fromAmount: 1, fromUnit: code, toAmount: link.factor, toUnit: link.unit }]
				: []
		),
		readable(amount, unit) {
			const base = baseUnit(unit);
			const total = inBase(amount, unit);
			const sizes = [
				{ code: base, factor: 1 },
				...units
					.filter((u) => u.active && u.link?.kind === 'base' && u.link.unit === base)
					.map((u) => ({ code: u.code, factor: u.link!.factor }))
			].filter((size) => size.factor <= total);
			const best = sizes.reduce(
				(max, size) => (size.factor > max.factor ? size : max),
				sizes[0] ?? { code: base, factor: 1 }
			);
			return `${formatAmount(total / best.factor)} ${best.code}`;
		}
	};
}

/**
 * An amount in another unit through the item's conversions and the platform's fixed measures, in as many steps as it
 * takes (as the server's `UnitConverter`); null when nothing joins the two measures.
 */
export function convertAmount(
	units: UnitSystem,
	conversions: readonly UnitConversion[],
	amount: number,
	fromUnit: string,
	toUnit: string
): number | null {
	const steps = [...units.fixedConversions, ...conversions].map((c) => ({
		a: units.baseUnit(c.fromUnit),
		b: units.baseUnit(c.toUnit),
		/** One base unit of `a` in base units of `b`. */
		ratio: units.inBase(c.toAmount, c.toUnit) / units.inBase(c.fromAmount, c.fromUnit)
	}));
	const goal = units.baseUnit(toUnit);
	// Breadth first over measures, each named by its base unit: a Map visits what is added while it is walked.
	const reached = new Map([[units.baseUnit(fromUnit), units.inBase(amount, fromUnit)]]);
	for (const [measure, inMeasure] of reached) {
		if (measure === goal) return inMeasure / units.inBase(1, toUnit);
		for (const { a, b, ratio } of steps) {
			if (a === measure && !reached.has(b)) reached.set(b, inMeasure * ratio);
			if (b === measure && !reached.has(a)) reached.set(a, inMeasure / ratio);
		}
	}
	return null;
}

/** "1 stk = 150 g", "60 g = 1 dl": a counted unit is named first, as it is how the item is thought of. */
export function conversionText(units: UnitSystem, conversion: UnitConversion): string {
	const { fromAmount, fromUnit, toAmount, toUnit } = conversion;
	const [first, second] =
		units.isCounted(toUnit) && !units.isCounted(fromUnit)
			? [units.readable(toAmount, toUnit), units.readable(fromAmount, fromUnit)]
			: [units.readable(fromAmount, fromUnit), units.readable(toAmount, toUnit)];
	return `${first} = ${second}`;
}

/** The same conversion, whichever way it was written: kg/g and sides normalised as the server stores it. */
function sameUnits(units: UnitSystem, a: UnitConversion, b: UnitConversion): boolean {
	const key = (c: UnitConversion) =>
		[units.baseUnit(c.fromUnit), units.baseUnit(c.toUnit)].sort().join('|');
	return key(a) === key(b);
}

/** The item's conversions with the new one added, replacing one between the same units. */
export function withConversion(
	units: UnitSystem,
	conversions: readonly UnitConversion[],
	conversion: UnitConversion
): UnitConversion[] {
	return [...conversions.filter((c) => !sameUnits(units, c, conversion)), conversion];
}

/**
 * The units "Saml" asks to convert between, e.g. 1 stk ≈ ? g, for an item still to buy in units the list could not
 * gather, and how many kinds of unit there are ("To enheder"); null when there is nothing to gather. The purchase
 * unit is asked about first, else a counted unit.
 */
export function unitsToGather(
	units: UnitSystem,
	itemUnits: readonly string[],
	purchaseUnit?: string | null
): { from: string; to: string; count: number } | null {
	const distinct = [...new Map(itemUnits.map((unit) => [units.baseUnit(unit), unit])).values()];
	if (distinct.length < 2) return null;
	const from =
		distinct.find(
			(unit) => purchaseUnit && units.baseUnit(unit) === units.baseUnit(purchaseUnit)
		) ??
		distinct.find(units.isCounted) ??
		distinct[0];
	const to = distinct.find((unit) => unit !== from)!;
	return { from, to, count: distinct.length };
}

type Quantity = { amount: number; unit: string };

/** What "1 from ≈ factor to" makes of the quantities in those two units, as "Saml" shows it before saving. */
export type GatherPreview = {
	/** The quantities as they stand, "2 stk", "250 g". */
	parts: string[];
	/** Their sum in the measure of `to`, "550 g". */
	total: string;
	/** What to buy in `from`, "4 stk". */
	buy: string;
	/** The exact amount ("3,7") when `from` is counted and it was rounded up to whole units; null otherwise. */
	roundedFrom: string | null;
};

/**
 * Sums the quantities in `from` and `to` with 1 `from` ≈ `factor` `to`; a counted `from` is bought whole, rounded up
 * once as the server does (`WholeUnits.Up`). Quantities in other units are left out: they are asked about next.
 */
export function gatherPreview(
	units: UnitSystem,
	quantities: readonly Quantity[],
	gather: { from: string; to: string },
	factor: number
): GatherPreview {
	const { from, to } = gather;
	const of = (unit: string) =>
		quantities.filter((q) => units.baseUnit(q.unit) === units.baseUnit(unit));
	const shown = [...of(from), ...of(to)];
	/** One `from` in the base unit of `to`. */
	const fromInTo = units.inBase(factor, to) / units.inBase(1, from);
	const total = shown.reduce(
		(sum, q) =>
			sum +
			(units.baseUnit(q.unit) === units.baseUnit(to)
				? units.inBase(q.amount, q.unit)
				: units.inBase(q.amount, q.unit) * fromInTo),
		0
	);
	const exact = total / fromInTo / units.inBase(1, from);
	const whole = units.isCounted(from) ? Math.ceil(exact - 1e-9) : exact;
	return {
		parts: shown.map((q) => `${formatAmount(q.amount)} ${q.unit}`),
		total: units.readable(total, units.baseUnit(to)),
		buy: units.isCounted(from)
			? `${formatAmount(whole)} ${from}`
			: units.readable(units.inBase(whole, from), units.baseUnit(from)),
		roundedFrom: Math.abs(whole - exact) > 1e-9 ? formatAmount(exact) : null
	};
}

/**
 * What is wrong with a conversion before it is sent, as the server's error code: an amount that is not above 0, or two
 * units of the same kind ("kg og g", which convert already); null when it can be sent.
 */
export function conversionProblem(
	units: UnitSystem,
	conversion: {
		fromAmount: number | null;
		fromUnit: string;
		toAmount: number | null;
		toUnit: string;
	}
): 'invalid_amount' | 'same_unit' | null {
	if (!conversion.fromAmount || conversion.fromAmount <= 0) return 'invalid_amount';
	if (!conversion.toAmount || conversion.toAmount <= 0) return 'invalid_amount';
	return units.baseUnit(conversion.fromUnit) === units.baseUnit(conversion.toUnit)
		? 'same_unit'
		: null;
}
